<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import { directive as vOnClickaway } from 'vue3-click-away';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: [Number, String], required: true },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['close']);

const store = useStore();
const cwToken = computed(() => store.getters.getCurrentUser?.access_token || '');

function detectDark() {
  if (document?.body?.classList?.contains('dark')) return '1';
  if (document?.documentElement?.classList?.contains('dark')) return '1';
  if (document?.documentElement?.style?.colorScheme === 'dark') return '1';
  return '0';
}

const iframeRef = ref(null);

const iframeSrc = computed(() => {
  const params = new URLSearchParams({
    tok: cwToken.value,
    conv: String(props.conversationId),
    inbox: String(props.inboxId),
    account: String(props.accountId),
    dark: detectDark(),
  });
  return `/stickers/picker?${params.toString()}`;
});

// ── Theme sync (parent → iframe) ────────────────────────────────
// Same pattern as ai-chat-theme-sync.js: watch body/html classes and push a
// postMessage whenever they change so the iframe can flip its own theme.
let themeObserver = null;

function pushTheme() {
  const win = iframeRef.value?.contentWindow;
  if (!win) return;
  const theme = detectDark() === '1' ? 'dark' : 'light';
  try {
    win.postMessage({ type: 'sz-sticker-theme', theme }, '*');
  } catch (_) {
    /* iframe not ready yet — onload handler will retry */
  }
}

function onIframeLoad() {
  pushTheme();
}

function startThemeObserver() {
  themeObserver = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.attributeName === 'class' || m.attributeName === 'style') {
        pushTheme();
        return;
      }
    }
  });
  if (document.body) {
    themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  });
}

// ── Delete-confirmation modal ───────────────────────────────────
// Iframe asks the parent to render the confirmation full-screen so the
// backdrop blur covers the whole viewport — not just the iframe's clipped area.
const deleteModal = ref({ open: false, id: null, url: '' });

function onMessage(e) {
  const d = e?.data || {};
  if (d?.type === 'sz-sticker-sent' || d?.type === 'sz-sticker-close') {
    emit('close');
    return;
  }
  if (d?.type === 'sz-sticker-delete-request' && d.id) {
    deleteModal.value = { open: true, id: d.id, url: d.url || '' };
  }
}

function confirmDelete() {
  const id = deleteModal.value.id;
  if (!id) return closeDeleteModal();
  iframeRef.value?.contentWindow?.postMessage(
    { type: 'sz-sticker-delete-confirm', id },
    '*'
  );
  closeDeleteModal();
}

function closeDeleteModal() {
  deleteModal.value = { open: false, id: null, url: '' };
}

function onEsc(e) {
  if (e.key !== 'Escape') return;
  if (deleteModal.value.open) closeDeleteModal();
  else emit('close');
}

function closeOnOutside() {
  // While the delete modal is up, it owns the outside-click semantics.
  if (deleteModal.value.open) return;
  emit('close');
}

onMounted(() => {
  window.addEventListener('message', onMessage);
  document.addEventListener('keydown', onEsc);
  startThemeObserver();
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  document.removeEventListener('keydown', onEsc);
  themeObserver?.disconnect();
});
</script>

<template>
  <div v-on-clickaway="closeOnOutside" class="sz-sticker-popover">
    <iframe
      ref="iframeRef"
      :src="iframeSrc"
      class="sz-sticker-iframe"
      title="Stickers"
      allow="clipboard-write"
      @load="onIframeLoad"
    />
  </div>

  <Teleport to="body">
    <div
      v-if="deleteModal.open"
      class="sz-sticker-delete-overlay"
      @click.self="closeDeleteModal"
    >
      <div class="sz-sticker-delete-modal" role="dialog" aria-modal="true">
        <div class="sz-delete-header">
          <h4>Confirmar exclusão</h4>
          <button class="sz-delete-close" aria-label="Fechar" @click="closeDeleteModal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div class="sz-delete-body">
          Tem certeza que deseja excluir esta figurinha? Após a exclusão, ela não poderá mais ser utilizada na conversa.
          <div class="sz-delete-preview">
            <img v-if="deleteModal.url" :src="deleteModal.url" alt="" />
          </div>
        </div>
        <div class="sz-delete-actions">
          <button class="sz-btn sz-btn-danger" @click="confirmDelete">Sim, excluir figurinha</button>
          <button class="sz-btn" @click="closeDeleteModal">Não, manter figurinha</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.sz-sticker-popover {
  position: absolute;
  /* Bottom = ReplyBottomPanel button row height + small gap — popover sits
     right above the sticker button, not above the whole reply box. */
  bottom: 60px;
  left: 8px;
  z-index: 9999;
  width: 340px;
  height: 380px;
  max-width: calc(100vw - 24px);
  max-height: 55vh;
  background: var(--color-background, #fff);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.sz-sticker-iframe {
  flex: 1;
  width: 100%;
  border: 0;
  /* Match the popover bg so there's no white flash before the iframe's own
     body background paints. Theme-synced by the :global(.dark) rule below. */
  background: var(--color-background, #fff);
}
:global(.dark) .sz-sticker-iframe,
:global(body.dark) .sz-sticker-iframe {
  background: #17191d;
}
:global(.dark) .sz-sticker-popover {
  background: #18181b;
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}
</style>

<style lang="scss">
/* Unscoped — the modal is teleported to <body> and would otherwise miss the
   component's data-v attribute. */
.sz-sticker-delete-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 100000;
}
.sz-sticker-delete-modal {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  color: #09090b;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.32);
  font-family: inherit;
}
.dark .sz-sticker-delete-modal,
body.dark .sz-sticker-delete-modal {
  background: #1f2125;
  color: #fafafa;
}

.sz-delete-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 10px;
}
.sz-delete-header h4 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}
.sz-delete-close {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #71717a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 0;
}
.sz-delete-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #09090b;
}
.dark .sz-delete-close,
body.dark .sz-delete-close {
  color: #9ca3af;
}
.dark .sz-delete-close:hover,
body.dark .sz-delete-close:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fafafa;
}

.sz-delete-body {
  padding: 4px 18px 14px;
  color: #52525b;
  font-size: 13px;
  line-height: 1.5;
}
.dark .sz-delete-body,
body.dark .sz-delete-body {
  color: #a1a1aa;
}
.sz-delete-preview {
  margin: 14px auto 4px;
  width: 96px;
  height: 96px;
  border-radius: 12px;
  background:
    linear-gradient(45deg, #f3f4f6 25%, transparent 25%),
    linear-gradient(-45deg, #f3f4f6 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f3f4f6 75%),
    linear-gradient(-45deg, transparent 75%, #f3f4f6 75%);
  background-color: #e5e7eb;
  background-size: 14px 14px;
  background-position: 0 0, 0 7px, 7px -7px, -7px 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dark .sz-delete-preview,
body.dark .sz-delete-preview {
  background:
    linear-gradient(45deg, #2a2d32 25%, transparent 25%),
    linear-gradient(-45deg, #2a2d32 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #2a2d32 75%),
    linear-gradient(-45deg, transparent 75%, #2a2d32 75%);
  background-color: #23262b;
  background-size: 14px 14px;
  background-position: 0 0, 0 7px, 7px -7px, -7px 0;
}
.sz-delete-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sz-delete-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 0 14px 14px;
}
.sz-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #ffffff;
  color: #09090b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  font-family: inherit;
}
.sz-btn:hover {
  background: rgba(0, 0, 0, 0.04);
}
.dark .sz-btn,
body.dark .sz-btn {
  background: #1f2125;
  color: #fafafa;
  border-color: rgba(255, 255, 255, 0.14);
}
.dark .sz-btn:hover,
body.dark .sz-btn:hover {
  background: rgba(255, 255, 255, 0.06);
}
.sz-btn-danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
}
.sz-btn-danger:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}
.dark .sz-btn-danger,
body.dark .sz-btn-danger {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
}
.dark .sz-btn-danger:hover,
body.dark .sz-btn-danger:hover {
  background: #dc2626;
  border-color: #dc2626;
}
</style>
